import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { isSupabaseConfigured } from '@/config';
import { useAuth } from '@/lib/auth/AuthProvider';
import {
  createFriendLeague,
  getFriendLeagueBoard,
  getMyFriendLeagues,
  joinFriendLeague,
  leaveFriendLeague,
} from '@/lib/data/provider';

const MY_KEY = ['friend-leagues'];

export function useMyFriendLeagues() {
  const { userId } = useAuth();
  return useQuery({
    queryKey: [...MY_KEY, userId ?? 'anon'],
    queryFn: getMyFriendLeagues,
    enabled: isSupabaseConfigured && Boolean(userId),
  });
}

export function useFriendLeagueBoard(leagueId: string | undefined) {
  return useQuery({
    queryKey: ['friend-league-board', leagueId],
    queryFn: () => getFriendLeagueBoard(leagueId as string),
    enabled: isSupabaseConfigured && Boolean(leagueId),
  });
}

export function useCreateFriendLeague() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { name: string; isDuel: boolean; seasonId?: string }) =>
      createFriendLeague(vars.name, vars.isDuel, vars.seasonId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MY_KEY }),
  });
}

export function useJoinFriendLeague() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => joinFriendLeague(code),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MY_KEY }),
  });
}

export function useLeaveFriendLeague() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (leagueId: string) => leaveFriendLeague(leagueId),
    onSuccess: (_data, leagueId) => {
      queryClient.invalidateQueries({ queryKey: MY_KEY });
      queryClient.invalidateQueries({ queryKey: ['friend-league-board', leagueId] });
    },
  });
}
